// tslint:disable:no-console
import {
    HubConnectionBuilder,
    HubConnection,
    HttpTransportType,
    LogLevel,
    HubConnectionState
} from '@aspnet/signalr';
import { injectable, inject } from 'inversify';
import { EventsServiceContract } from '@/core/contracts/eventsServiceContract';
import { CONTRACTS, AuthServiceContract, SignInType, ConfigServiceContract } from '@/core/contracts';
import config from '@/config';
import { ConnectionStatusState, SettingsState, UserState } from '@/store';
import { ConnectionStatus } from '@/dto/ConnectionStatus';

type Handler = (args: any[]) => void;

/**
 * Server and client events.
 * @export
 * @class EventsService
 * @implements {EventsServiceContract}
 */
@injectable()
export class EventsService implements EventsServiceContract {
    private _connection?: HubConnection;
    private _handlers: { [id: string]: Handler[] } = {};
    private _countTryConnection: number = 0;

    constructor(
        @inject(CONTRACTS.AuthService) private _authService: AuthServiceContract,
        @inject(CONTRACTS.ConfigService) private _configService: ConfigServiceContract
    ) {
        /* empty constructor*/
    }

    public async startupAsync(): Promise<void> {
        if (this._connection === undefined) {
            this._connection = new HubConnectionBuilder()
                .withUrl(`${this._configService.getConfig().Services?.identityURL}/eventsHub`, {
                    transport: HttpTransportType.LongPolling,
                    accessTokenFactory: () => {
                        return this._authService.accessToken || '';
                    }
                })
                .configureLogging(LogLevel.Critical)
                .build();

            this._connection.on('processEvent', data =>
                this.processEvent(data)
            );
            this._connection.onclose(() => {
                ConnectionStatusState.changeStatus(
                    ConnectionStatus.TempDisconnect
                );
                this.reconnect();
            });

            this.connect();
        }
    }

    public on(event: string, handler: Handler): void {
        if (this._connection) {
            this.storeHandler(event, handler);
            this._connection!.on(event, handler);
        }
    }

    public off(event: string, handler: Handler): void {
        if (this._connection) {
            this.deleteHandler(event, handler);
            this._connection!.off(event, handler);
        }
    }

    public raise(event: string, args: any): void {
        const handlers = this.getHandlers(event);
        for (const handler of handlers) {
            handler(args);
        }
    }

    public reconnect(): void {
        const timeout = config.events.signalRReconnectTimeout;
        console.info(`reconnecting in ${timeout} ms`);
        setTimeout(() => {
            ConnectionStatusState.changeStatus(ConnectionStatus.Reconnecting);
            console.info(`reconnecting...`);
            this.connect();
        }, timeout);
    }

    private connect(): Promise<any> {
        return this._connection!.start()
            .catch(error => {
                if (
                    error &&
                    (error.statusCode === 403 || error.statusCode === 401)
                ) {
                    this._authService!.signIn(SignInType.Default);
                }
            })
            .finally(() => {
                const connectionStatus =
                    this._connection!.state === HubConnectionState.Connected
                        ? 1
                        : 0;
                if (connectionStatus === 0) {
                    if (
                        this._countTryConnection <
                        config.events.countTryConnection
                    ) {
                        this._countTryConnection++;
                        ConnectionStatusState.changeStatus(
                            ConnectionStatus.TempDisconnect
                        );
                        console.info(
                            `Временная потеря связи, попытка: ${this._countTryConnection}`
                        );
                        this.reconnect();
                    } else {
                        ConnectionStatusState.changeStatus(
                            ConnectionStatus.Disconnected
                        );
                        if (
                            SettingsState.contextSettings.global
                                .autoReconnect === undefined ||
                            SettingsState.contextSettings.global.autoReconnect
                        ) {
                            this.reconnect();
                        }
                    }
                }

                if (connectionStatus === 1) {
                    this._countTryConnection = 0;
                    ConnectionStatusState.changeStatus(
                        ConnectionStatus.Connected
                    );
                    setTimeout(() => {
                        ConnectionStatusState.changeStatus(
                            ConnectionStatus.NotSet
                        ); // удаляем индикатор соединения
                    }, 5000);
                }
            });
    }

    private storeHandler(event: string, handler: Handler): void {
        let handlers = this._handlers[event];

        if (handlers === undefined) {
            handlers = [];
            this._handlers[event] = handlers;
        }

        handlers.push(handler);
    }

    private deleteHandler(event: string, handler: Handler): void {
        const handlers = this._handlers[event];

        if (handlers !== undefined) {
            this._handlers[event] = handlers.filter(x => x !== handler);
        }
    }

    private getHandlers(event: string): Handler[] {
        return this._handlers[event] || [];
    }

    private processEvent(data: any) {
        // IsDeleted
    }
}
